import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Upload, Link2, Trash2, Loader2, FileText, ExternalLink } from "lucide-react";
import { api, API, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";

const GROUPS = [
  { title: "Latest PDF (the R.O.A.N framework)", variants: [
    { kind: "latest_pdf", label: "Email & socials version", hint: "Smaller file for sharing — “Download for email & socials” button" },
    { kind: "latest_pdf_print", label: "Print-friendly version", hint: "High-res / print layout — “Print-friendly version” button" },
  ] },
  { title: "Flyer", variants: [
    { kind: "flyer", label: "Email & socials version", hint: "Shown under “The ROAN flyer”" },
    { kind: "flyer_print", label: "Print-friendly version", hint: "Bleed/CMYK or A4 print layout" },
  ] },
  { title: "Palm cards — full pack", variants: [
    { kind: "cards_digital", label: "Email & socials version", hint: "If empty, the site auto-builds a PDF from the talking cards below" },
    { kind: "cards_print", label: "Print-friendly version", hint: "Your print-ready pack (e.g. 2-up A4 with crop marks)" },
  ] },
  { title: "Footer documents", variants: [
    { kind: "terms", label: "Terms and Conditions", hint: "Linked from the footer" },
    { kind: "privacy", label: "Privacy Policy", hint: "Linked from the footer" },
    { kind: "charter", label: "Our Charter", hint: "Linked from the footer" },
  ] },
];

const DocCard = ({ kind, label, hint, doc, refresh }) => {
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState("");

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/documents/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${label} uploaded`);
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const saveLink = async (e) => {
    e.preventDefault();
    if (!link.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/documents/${kind}/link`, { url: link.trim() });
      toast.success(`${label} linked`);
      setLink("");
      refresh();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/documents/${kind}`);
      toast.success(`${label} removed`);
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid={`admin-document-card-${kind}`} className="rounded-2xl bg-[#1B2A41] border border-[#2C3E55] p-5 space-y-4">
      <div>
        <h3 className="font-type font-bold text-lg">{label}</h3>
        <p className="text-xs text-[#F8FAFC]/50">{hint}</p>
      </div>
      {doc ? (
        <div className="flex items-center gap-3 p-3 rounded-xl bg-[#121E2B] border border-[#2C3E55]" data-testid={`admin-document-current-${kind}`}>
          <FileText className="w-5 h-5 text-[#8FA58A]" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate">{doc.original_filename}</p>
            <p className="text-[11px] text-[#F8FAFC]/50">{doc.external_url ? "External link" : `${(doc.size / 1024).toFixed(0)} KB`} · {new Date(doc.created_at).toLocaleDateString("en-AU")}</p>
          </div>
          <a href={`${API}/documents/${kind}/download`} target="_blank" rel="noreferrer" className="p-2 rounded-lg hover:bg-[#2C3E55]" title="Open"><ExternalLink className="w-4 h-4" /></a>
          <button onClick={remove} data-testid={`admin-document-remove-${kind}`} className="p-2 rounded-lg text-[#F8FAFC]/60 hover:text-[#E2705A] hover:bg-[#E2705A]/10"><Trash2 className="w-4 h-4" /></button>
        </div>
      ) : (
        <p className="text-sm text-[#F8FAFC]/50 italic">Nothing published yet — the landing page shows “Coming soon”.</p>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.docx" className="hidden" data-testid={`admin-document-file-input-${kind}`} onChange={(e) => upload(e.target.files?.[0])} />
          <button type="button" disabled={busy} onClick={() => fileRef.current?.click()} data-testid={`admin-document-upload-button-${kind}`} className="w-full h-11 rounded-full bg-[#E2705A] hover:bg-[#D35D46] disabled:opacity-60 font-type font-bold uppercase tracking-wider text-xs inline-flex items-center justify-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload file
          </button>
        </div>
        <form onSubmit={saveLink} className="flex gap-2">
          <Input data-testid={`admin-document-link-input-${kind}`} value={link} onChange={(e) => setLink(e.target.value)} placeholder="…or paste a link (Canva, Drive)" className="h-11 bg-[#121E2B] border-[#2C3E55]" />
          <button type="submit" disabled={busy || !link.trim()} data-testid={`admin-document-link-button-${kind}`} className="h-11 w-11 flex-shrink-0 rounded-full bg-[#8FA58A] hover:bg-[#7A9375] disabled:opacity-60 text-[#121E2B] inline-flex items-center justify-center"><Link2 className="w-4 h-4" /></button>
        </form>
      </div>
    </div>
  );
};

export const DocumentsManager = () => {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["documents"], queryFn: () => api.get("/documents").then((r) => r.data) });
  const byKind = Object.fromEntries(data.map((d) => [d.kind, d]));
  const refresh = () => qc.invalidateQueries({ queryKey: ["documents"] });
  return (
    <div className="space-y-8">
      {GROUPS.map((g) => (
        <section key={g.title} data-testid={`admin-document-group-${g.variants[0].kind}`}>
          <h2 className="font-type font-bold text-xs uppercase tracking-[0.25em] text-[#8FA58A] mb-3">{g.title}</h2>
          <div className="grid lg:grid-cols-2 gap-5">
            {g.variants.map((k) => <DocCard key={k.kind} {...k} doc={byKind[k.kind]} refresh={refresh} />)}
          </div>
        </section>
      ))}
    </div>
  );
};
